import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { Send, FileText, Upload, LogOut, Menu, X, Info, Github, Linkedin, Mail, Trash2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import './AppChatGPT.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function AppChatGPT({ user, logout }) {
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showAbout, setShowAbout] = useState(false);
  const messagesContainerRef = useRef(null);

  // Toast notification helper
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  useEffect(() => {
    if (selectedDocument) {
      loadChatHistory(selectedDocument.document_id);
    }
  }, [selectedDocument]);

  // Auto-scroll only when new message arrives
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const loadChatHistory = async (documentId) => {
    try {
      const response = await axios.get(`${API_URL}/chat/history/${documentId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading chat history:', error);
      setMessages([]);
    }
  };

  const handleDocumentSelect = (doc) => {
    setSelectedDocument(doc);
    // Close sidebar on mobile after selecting document
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (!file.name.endsWith('.pdf')) {
      showToast('Please upload a PDF file', 'error');
      return;
    }

    setUploading(true);
    setUploadProgress('Uploading document...');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      
      setUploadProgress('Processing and chunking document...');
      const response = await axios.post(`${API_URL}/documents/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      setUploadProgress('Creating embeddings...');
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief pause for UX
      
      showToast(`✅ ${file.name} uploaded! ${response.data.chunk_count} chunks created.`, 'success');
      loadDocuments();
    } catch (error) {
      console.error('Upload error:', error);
      showToast(`❌ Upload failed: ${error.response?.data?.detail || error.message}`, 'error');
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false
  });

  const deleteDocument = async (docId, docName, e) => {
    e.stopPropagation(); // Prevent selecting the document when clicking delete
    
    if (!window.confirm(`Are you sure you want to delete "${docName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/documents/${docId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`
        }
      });
      
      // If the deleted document was selected, clear selection
      if (selectedDocument?.document_id === docId) {
        setSelectedDocument(null);
        setMessages([]);
      }
      
      // Reload documents list
      loadDocuments();
      showToast(`🗑️ "${docName}" deleted successfully!`, 'success');
    } catch (error) {
      console.error('Delete error:', error);
      showToast(`❌ Delete failed: ${error.response?.data?.detail || error.message}`, 'error');
    }
  };

  const askQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim() || !selectedDocument) return;

    const userQuestion = question;
    setQuestion('');
    setLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: userQuestion }]);

    try {
      const response = await axios.post(`${API_URL}/query`, {
        question: userQuestion,
        document_id: selectedDocument.document_id
      });

      if (response.data.answer) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.data.answer,
          sources: response.data.sources
        }]);
      }
    } catch (error) {
      console.error('Query error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your question.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="gpt-app">
      {/* Header */}
      <header className="gpt-header">
        <button className="menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <h1>
          {selectedDocument ? (
            <>
              <FileText size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              {selectedDocument.filename}
            </>
          ) : (
            'Documind'
          )}
        </h1>
        <div className="header-actions">
          <button onClick={() => setShowAbout(true)} className="info-btn" title="About">
            <Info size={18} />
          </button>
          <span className="user-badge">{user.username}</span>
          <button onClick={logout} className="logout-btn" title="Logout">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* About Modal */}
      {showAbout && (
        <div className="modal-overlay" onClick={() => setShowAbout(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowAbout(false)}>
              <X size={24} />
            </button>
            
            <div className="about-content">
              <div className="about-header">
                <div className="about-avatar">MS</div>
                <div className="about-title">
                  <h2>Muhammad Saad Khan</h2>
                  <p className="about-subtitle">AI/ML Engineer & Full-Stack Developer</p>
                </div>
              </div>

              <div className="about-section">
                <h3>🎓 Education</h3>
                <p>Graduate from <strong>University of Swabi</strong></p>
              </div>

              <div className="about-section">
                <h3>💡 About Me</h3>
                <p>
                  Passionate about building intelligent AI applications and working at the intersection 
                  of artificial intelligence and practical software engineering. I specialize in creating 
                  applied AI solutions that solve real-world problems.
                </p>
              </div>

              <div className="about-section">
                <h3>🚀 Expertise</h3>
                <div className="skills-grid">
                  <div className="skill-item">
                    <span className="skill-icon">🐍</span>
                    <div>
                      <strong>Python Development</strong>
                      <p>Strong command in building robust Python applications</p>
                    </div>
                  </div>
                  <div className="skill-item">
                    <span className="skill-icon">🤖</span>
                    <div>
                      <strong>AI/ML Engineering</strong>
                      <p>Applied AI, RAG systems, and LLM applications</p>
                    </div>
                  </div>
                  <div className="skill-item">
                    <span className="skill-icon">📊</span>
                    <div>
                      <strong>Data Analysis</strong>
                      <p>Expert in NumPy, Pandas, and data visualization</p>
                    </div>
                  </div>
                  <div className="skill-item">
                    <span className="skill-icon">🔍</span>
                    <div>
                      <strong>Feature Engineering</strong>
                      <p>Extracting meaningful insights from complex datasets</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="about-section">
                <h3>🎯 Vision</h3>
                <p>
                  Aspiring to become a leading AI/ML practitioner, creating innovative solutions 
                  that leverage the power of artificial intelligence to make technology more 
                  accessible and intelligent. Committed to continuous learning and pushing the 
                  boundaries of what's possible with AI.
                </p>
              </div>

              <div className="about-section">
                <h3>🛠️ This Project</h3>
                <p>
                  <strong>Documind</strong> is an intelligent document assistant that demonstrates 
                  the practical application of RAG (Retrieval-Augmented Generation) technology, 
                  combining FastAPI, React, LangChain, and ChromaDB to create a seamless 
                  document Q&A experience.
                </p>
              </div>

              <div className="about-footer">
                <h3>📫 Connect With Me</h3>
                <div className="social-links">
                  <a href="https://github.com/saadkhan2003" target="_blank" rel="noopener noreferrer" className="social-link">
                    <Github size={20} />
                    <span>GitHub</span>
                  </a>
                  <a href="mailto:msaad.official6@gmail.com" className="social-link">
                    <Mail size={20} />
                    <span>Email</span>
                  </a>
                  <a href="https://linkedin.com/in/saadkhan2003/" target="_blank" rel="noopener noreferrer" className="social-link">
                    <Linkedin size={20} />
                    <span>LinkedIn</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="gpt-layout">
        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
        )}
        
        {/* Sidebar */}
        <aside className={`gpt-sidebar ${sidebarOpen ? 'open' : ''}`}>
          {/* Upload */}
          <div className="sidebar-section">
            <h3><Upload size={18} /> Upload PDF</h3>
            <div {...getRootProps()} className={`dropzone-mini ${isDragActive ? 'active' : ''} ${uploading ? 'uploading-active' : ''}`}>
              <input {...getInputProps()} disabled={uploading} />
              {uploading ? (
                <div className="upload-progress">
                  <div className="circular-progress">
                    <svg className="progress-ring" width="60" height="60">
                      <circle
                        className="progress-ring-circle"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="transparent"
                        r="26"
                        cx="30"
                        cy="30"
                      />
                    </svg>
                  </div>
                  <span className="upload-text">{uploadProgress}</span>
                </div>
              ) : (
                <>
                  <FileText size={32} />
                  <p>{isDragActive ? 'Drop here' : 'Drag & drop or click'}</p>
                </>
              )}
            </div>
          </div>

          {/* Documents */}
          <div className="sidebar-section">
            <h3><FileText size={18} /> Documents ({documents.length})</h3>
            <div className="documents-list-mini">
              {documents.length === 0 ? (
                <p className="empty-msg">No documents yet</p>
              ) : (
                documents.map(doc => (
                  <div
                    key={doc.document_id}
                    className={`doc-item ${selectedDocument?.document_id === doc.document_id ? 'selected' : ''}`}
                    onClick={() => handleDocumentSelect(doc)}
                  >
                    <FileText size={16} />
                    <div className="doc-info">
                      <div className="doc-name">{doc.filename}</div>
                      <div className="doc-meta">{doc.chunk_count} chunks</div>
                    </div>
                    <button
                      className="delete-btn"
                      onClick={(e) => deleteDocument(doc.document_id, doc.filename, e)}
                      title="Delete document"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </aside>

        {/* Main Chat */}
        <main className="gpt-main">
          <div className="gpt-chat-container">
            {!selectedDocument ? (
              <div className="gpt-welcome">
                <div className="welcome-icon">AI</div>
                <h2>Welcome to Documind</h2>
                <p>AI Document Assistant - Upload a PDF and start asking questions</p>
                <div className="welcome-features">
                  <div className="feature">🤖 Advanced RAG Technology</div>
                  <div className="feature">📝 Markdown Formatting</div>
                  <div className="feature">📚 Source Citations</div>
                </div>
                <div className="welcome-footer">
                  <p className="built-by">
                    Built with ❤️ by <strong>Muhammad Saad Khan</strong>
                  </p>
                  <p className="built-tech">
                    FastAPI • React • LangChain • ChromaDB
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="gpt-messages" ref={messagesContainerRef}>
                  {messages.length === 0 && (
                    <div className="empty-chat">
                      <p>Start a conversation about this document...</p>
                    </div>
                  )}
                  {messages.map((msg, idx) => (
                    <div key={idx} className={`gpt-message ${msg.role}`}>
                      {msg.role === 'assistant' && (
                        <div className="gpt-avatar">AI</div>
                      )}
                      <div className="gpt-message-wrapper">
                        <div className="gpt-message-content">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                        {msg.sources && msg.sources.length > 0 && (
                          <div className="sources-compact">
                            <strong>Sources:</strong>
                            {msg.sources.slice(0, 2).map((source, i) => (
                              <div key={i} className="source-tag">
                                Chunk {source.metadata.chunk_index + 1}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="gpt-message assistant">
                      <div className="gpt-avatar">AI</div>
                      <div className="gpt-message-wrapper">
                        <div className="gpt-message-content">
                          <div className="gpt-typing-indicator">
                            <span></span><span></span><span></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <form className="gpt-input-form" onSubmit={askQuestion}>
                  <input
                    type="text"
                    value={question}
                    onChange={e => setQuestion(e.target.value)}
                    placeholder="Ask a question about this document..."
                    disabled={loading}
                  />
                  <button type="submit" disabled={loading || !question.trim()}>
                    <Send size={20} />
                  </button>
                </form>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}
